/**
 * Сторінка оформлення: підсвітка способу доставки, маска телефону, помилки.
 *
 * Усе, що робить сам чекаут — перерахунок підсумків, валідація, сабміт —
 * лишається за WooCommerce (checkout.js плагіна). Тут тільки те, чого він не
 * знає: класи верстки.
 *
 * Чому не main.js. Той файл — збілджений код верстки (§7.5 CLAUDE.md), і
 * форму він шукає рівно за '.base-form' — це AJAX-форми теми. Форма
 * оформлення має клас '.form-field' (стилі полів), але не '.base-form': її
 * сабміт веде WooCommerce, і перехоплювати його не можна. Тож маска й стани
 * помилок, які main.js дає всім іншим формам, сюди не дістаються — і
 * повторені тут.
 *
 * ── Підсвітка способу доставки ───────────────────────────────────────────
 *
 * Радіокнопки лежать у лівій колонці, ЗА межами #order_review, який
 * WooCommerce перемальовує на кожну зміну доставки. Тому слухач ставиться
 * один раз і переживає всі оновлення — переприв'язувати його на
 * 'updated_checkout' не треба.
 *
 * ── Помилки ──────────────────────────────────────────────────────────────
 *
 * WooCommerce вішає на '.form-row' свої 'woocommerce-invalid' /
 * 'woocommerce-validated', під які в темі немає жодного стилю: верстка
 * малює помилку класом 'is-require' на '.field'. Дзеркалимо через
 * MutationObserver, а не через ті ж події: обробники WooCommerce —
 * jQuery-делеговані, і порядок виконання відносно нативного слухача не
 * гарантований. Спостерігач за атрибутом спрацьовує вже за фактом.
 */

import IMask from 'imask';

// Той самий формат, що в main.js для решти форм сайту: покупець, який уже
// підписувався на новини, вводить телефон однаково в обох місцях.
const PHONE_MASK = { mask: '+{38} (000) 000 00 00' };

const syncShippingActive = (form) => {
  form.querySelectorAll('.shipping-methods__item').forEach((item) => {
    const input = item.querySelector('input[name^="shipping_method"]');

    item.classList.toggle('is-active', Boolean(input?.checked));
  });
};

const initPhone = (form) => {
  form.querySelectorAll('input[type="tel"]').forEach((input) => {
    if (input.dataset.nmhgMasked) return;

    IMask(input, PHONE_MASK);
    input.dataset.nmhgMasked = '1';
  });
};

/** 'woocommerce-invalid' на .form-row -> 'is-require' на .field. */
const syncErrors = (form) => {
  const observer = new MutationObserver((records) => {
    records.forEach(({ target }) => {
      if (!(target instanceof HTMLElement) || !target.classList.contains('form-row')) return;

      target.classList.toggle('is-require', target.classList.contains('woocommerce-invalid'));
    });
  });

  observer.observe(form, {
    subtree: true,
    attributes: true,
    attributeFilter: ['class'],
  });
};

/**
 * Розгортає посилання-якорі в списку помилок назад у текст.
 *
 * WooCommerce 11 обгортає кожну помилку, прив'язану до поля, у <a href="#id">
 * (wrapMessagesInsideLink() у його checkout.js), щоб клік прокручував до
 * поля. На цьому сайті шапка зафіксована: якір прокручує так, що поле
 * ховається під нею, тобто посилання веде «нікуди» й лише збиває з пантелику.
 *
 * Тому лишаємо чистий текст — це просто сповіщення. Прокрутку до самого
 * списку WooCommerce робить сам (scroll_to_notices), і вона працює.
 *
 * Спостерігач ОБОВ'ЯЗКОВО в змінній: без посилання на нього об'єкт нічим не
 * утримується й може піти під збирач сміття разом зі спостереженням.
 */
const unwrapNoticeLinks = (form) => {
  const strip = (root) =>
    root.querySelectorAll('.woocommerce-error li > a[href^="#"]').forEach((link) => {
      link.replaceWith(...link.childNodes);
    });

  const observer = new MutationObserver((records) => {
    records.forEach((record) =>
      record.addedNodes.forEach((node) => {
        if (node.nodeType === 1) strip(node);
      }),
    );
  });

  observer.observe(form, { childList: true });

  // Сторінка могла відкритись уже з помилками — сабміт без JS, наприклад.
  strip(form);
};

const init = () => {
  const form = document.querySelector('form.woocommerce-checkout');

  if (!form) return;

  syncShippingActive(form);
  initPhone(form);
  syncErrors(form);
  unwrapNoticeLinks(form);

  form.addEventListener('change', (event) => {
    if (event.target.matches('input[name^="shipping_method"]')) syncShippingActive(form);
  });

  // Пробіл і Enter на .shipping-methods__label: у верстці в лейбла є
  // tabindex, тобто він задуманий як самостійна точка фокуса, а браузер
  // клавішу на лейблі сам не обробляє — лише мишу.
  form.addEventListener('keydown', (event) => {
    const label = event.target.closest?.('.shipping-methods__label');

    if (!label || (event.key !== ' ' && event.key !== 'Enter')) return;

    event.preventDefault();
    label.click();
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
